'use client';
import { getCourseContentTexts } from '@eliadmin/core/src/utils/project-content-text';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Course } from '@eliadmin/core/src/api/courses/coursers-api';

import { AddCourseToFavoritesButton } from '@eliadmin/core/src/components/courses/add-course-to-favorites-button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuAlarmClock, LuCalendarDays } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { CourseLevel } from './course-level';

const FREE_COURSE_ID = 58;

export type CourseCardProps = {
  course: Course;
  className?: {
    main?: string;
  };
  isBought?: boolean;
  selectedLecturerId?: number;
  headingAs?: 'h2' | 'h3';
};

function CourseCardInternal({
  course,
  className,
  isBought,
  selectedLecturerId,
  headingAs = 'h3',
}: CourseCardProps) {
  const { t } = useTranslations();
  const HeadingTag = headingAs;

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <div
      className={cn('col-span-1 flex w-full flex-col items-start justify-start', className?.main)}
    >
      <div className="relative h-[240px] w-full sm:h-[255px] lg:h-[277px]">
        <div className="absolute left-4 top-4 z-10 flex w-full flex-wrap items-start justify-start gap-2 pr-16">
          {course.id === FREE_COURSE_ID && (
            <span className="bg-primary px-1.5 py-[4px] text-[10px] font-bold uppercase text-white sm:text-xs md:px-2 md:py-0.5 md:text-sm">
              Zdarma
            </span>
          )}
          {course.levels && course.levels.length > 0 && <CourseLevel course={course} />}
        </div>
        {!isBought && (
          <div
            onClick={e => e.stopPropagation()}
            className={cn('absolute right-4 top-4 z-10', 'ea-course-card__add-to-favorites')}
          >
            <AddCourseToFavoritesButton
              className={{
                button: 'bg-transparent p-0 text-white hover:bg-transparent',
                container: 'bg-transparent p-0 text-white',
                innerContainer: 'bg-transparent p-0 !text-white',
                icon: cn('stroke-white text-white', 'ea-course-card__add-to-favorites__icon'),
              }}
              course={{
                slug: course.slug,
                is_favorite: !!course.is_favorite,
              }}
            />
          </div>
        )}
        <Link
          className="absolute inset-0 z-[1] h-full w-full bg-black/30"
          prefetch={false}
          href={course.slug}
          aria-label={course.name}
        />
        {!!getCourseContentTexts(course)?.length &&
          getCourseContentTexts(course).find(field => field.id === 2) && (
            <div className="absolute bottom-0 left-0 z-[2] flex w-full items-center justify-start gap-2 bg-black px-4 py-2">
              <Image
                src={'/images/music.svg'}
                alt={
                  getCourseContentTexts(course).find(field => field.id === 2)?.value || 'Music Icon'
                }
                width={14}
                height={14}
              />
              <p className="text-xs font-normal text-white">
                {getCourseContentTexts(course).find(field => field.id === 2)?.value}
              </p>
            </div>
          )}

        <Image
          src={course?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(course?.image, course?.name, course?.name ?? 'Kurz')}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="(min-width: 1024px) 392px, (min-width: 640px) 360px, 340px"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-3 bg-[#F6F6F6] p-4 sm:gap-4 sm:p-5 md:p-6">
        <Link prefetch={false} href={course.slug}>
          <HeadingTag
            data-ea-visual-tag={headingAs === 'h2' ? 'h3' : undefined}
            className="line-clamp-2 h-14 w-full text-balance font-heading text-xl font-medium text-black md:h-16 md:text-2xl"
          >
            {course.name}
          </HeadingTag>
        </Link>

        {course.lecturers &&
          course.lecturers.length > 0 &&
          (selectedLecturerId ? (
            <Link
              prefetch={false}
              href={course.lecturers.find(l => l.id === selectedLecturerId)?.slug ?? ''}
              className="flex w-full items-center justify-start gap-2"
            >
              <Image
                src={
                  course.lecturers.find(l => l.id === selectedLecturerId)?.image?.url ??
                  '/images/user.png'
                }
                alt={resolveImageAlt(
                  course?.lecturers?.find?.(l => l.id === selectedLecturerId)?.image,
                  course?.lecturers?.find?.(l => l.id === selectedLecturerId)?.name ??
                    'Meno Priezvisko',
                )}
                width={0}
                height={0}
                className="max-h-10 min-h-10 min-w-10 max-w-10 rounded-full border-2 border-white object-cover object-center"
                sizes="40px"
              />
              <div className="flex w-full flex-col items-start justify-start">
                <span className="text-xs font-bold text-black/70">{t('text2256')}</span>
                <p className="text-sm font-normal text-black">
                  {course.lecturers.find(l => l.id === selectedLecturerId)?.name}
                </p>
              </div>
            </Link>
          ) : (
            <Link
              prefetch={false}
              href={course.lecturers[0].slug}
              className="flex w-full items-center justify-start gap-2"
            >
              <Image
                src={course.lecturers[0]?.image?.url ?? '/images/user.png'}
                alt={resolveImageAlt(
                  course?.lecturers?.[0]?.image,
                  course?.lecturers?.[0]?.name ?? 'Meno Priezvisko',
                )}
                width={0}
                height={0}
                className="max-h-10 min-h-10 min-w-10 max-w-10  rounded-full border-2 border-white object-cover object-center"
                sizes="40px"
              />
              <div className="flex w-full flex-col items-start justify-start">
                <span className="text-xs font-bold text-black/70">{t('text2256')}</span>
                <p className="text-sm font-normal text-black">{course.lecturers[0]?.name}</p>
              </div>
            </Link>
          ))}

        <Link
          prefetch={false}
          href={course.slug}
          className="flex w-full items-center justify-start gap-2 sm:gap-3 md:gap-4"
        >
          <CourseIconLabel label={course.categories[0]?.name ?? ''} image="/images/ballet.svg" />
          {course.date_start && (
            <CourseIconLabel
              label={formatDate(course.date_start)}
              icon={<LuCalendarDays className="min-h-4 min-w-4 text-primary" />}
            />
          )}
          <CourseIconLabel
            label={`${course.duration} min.`}
            icon={<LuAlarmClock className="min-h-4 min-w-4 text-primary" />}
          />
        </Link>
      </div>
    </div>
  );
}

export function CourseCard(props: CourseCardProps) {
  return <CourseCardInternal key={`course-${props.course.id ?? props.course.slug}`} {...props} />;
}

const CourseIconLabel = ({
  icon,
  image,
  label,
}: {
  icon?: React.ReactNode;
  image?: string;
  label: string;
}) => {
  return (
    <div className="flex items-center justify-start gap-2">
      {icon ? (
        icon
      ) : (
        <Image
          src={image ?? '/images/placeholder.png'}
          alt={label}
          width={0}
          height={0}
          className="min-h-4 min-w-4"
        />
      )}
      <p className="text-xs font-normal text-black">{label}</p>
    </div>
  );
};
